import { MONOREPO_LAYOUT, MonorepoLayoutRoot } from "./monorepo-layout";

/**
 * Parsed shape of an npm-style scoped package name (`@scope/name`).
 */
interface ParsedScopedName {
  readonly scope: string;
  readonly name: string;
}

/**
 * Parse `@scope/name` into its two halves. Returns `undefined` when the
 * input is unscoped or otherwise not a valid scoped package name.
 *
 * Matches the npm package-name grammar loosely: the scope must start with
 * `@`, both scope and name must be non-empty, and neither may contain a
 * slash. This is good enough to decide whether to derive an `outdir` —
 * projen itself already validates the full name elsewhere.
 */
function parseScopedName(input: string): ParsedScopedName | undefined {
  if (!input.startsWith("@")) {
    return undefined;
  }
  const slashIndex = input.indexOf("/");
  if (slashIndex < 2) {
    // Either no slash, or the scope portion is empty (`@/foo`).
    return undefined;
  }
  const scope = input.slice(0, slashIndex);
  const name = input.slice(slashIndex + 1);
  if (name.length === 0 || name.includes("/")) {
    return undefined;
  }
  return { scope, name };
}

/**
 * Resolve a sub-project's `outdir` from its package name.
 *
 * Given a scoped package name like `@codedrifters/foo`, returns
 * `<root>/@codedrifters/foo` where `<root>` comes from
 * {@link MONOREPO_LAYOUT}. The scope segment is preserved verbatim —
 * including the leading `@` — so the resulting path mirrors the npm
 * name exactly (e.g. `packages/@codedrifters/foo`).
 *
 * Throws when the package name is unscoped or empty. Callers must
 * either supply a scoped name or pass `outdir` explicitly.
 *
 * @param packageName - The npm package name. Typically sourced from
 *   `userOptions.packageName ?? userOptions.name` — both are valid
 *   inputs because projen itself falls back from `packageName` to
 *   `name` when rendering `package.json`.
 * @param root - Which top-level monorepo folder to place the
 *   sub-project under (e.g. `packages`, `apps`, `sites`).
 */
export function resolveOutdirFromPackageName(
  packageName: string | undefined,
  root: MonorepoLayoutRoot,
): string {
  if (packageName === undefined || packageName.length === 0) {
    throw new Error(
      "Cannot derive outdir: packageName (or name) is required when outdir is omitted. " +
        "Either pass a scoped `packageName` / `name` like `@scope/foo`, or set `outdir` explicitly.",
    );
  }

  const parsed = parseScopedName(packageName);
  if (parsed === undefined) {
    throw new Error(
      `Cannot derive outdir from unscoped packageName "${packageName}". ` +
        "Either use a scoped name like `@scope/foo`, or set `outdir` explicitly. " +
        `Configulator places sub-projects at "${root}/<scope>/<name>".`,
    );
  }

  return `${root}/${parsed.scope}/${parsed.name}`;
}

/**
 * Convenience wrapper that resolves an `outdir` for a `TypeScriptProject`
 * under the canonical `packages/` root.
 */
export function resolveTypeScriptProjectOutdir(
  packageName: string | undefined,
): string {
  return resolveOutdirFromPackageName(packageName, MONOREPO_LAYOUT.PACKAGES);
}

/**
 * Convenience wrapper that resolves an `outdir` for an `AwsCdkProject`
 * under the canonical `apps/` root.
 */
export function resolveAwsCdkProjectOutdir(
  packageName: string | undefined,
): string {
  return resolveOutdirFromPackageName(packageName, MONOREPO_LAYOUT.APPS);
}

/**
 * Convenience wrapper that resolves an `outdir` for an `AstroProject`
 * under the canonical `sites/` root.
 */
export function resolveAstroProjectOutdir(
  packageName: string | undefined,
): string {
  return resolveOutdirFromPackageName(packageName, MONOREPO_LAYOUT.SITES);
}

/**
 * Convenience wrapper that resolves an `outdir` for a
 * `ReactViteSiteProject` under the canonical `sites/` root.
 */
export function resolveReactViteSiteProjectOutdir(
  packageName: string | undefined,
): string {
  return resolveOutdirFromPackageName(packageName, MONOREPO_LAYOUT.SITES);
}
